import { formatPercent } from '@angular/common';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { AccountModel, InvestmentAccountsClient, OrionEclipseAccountDetailModel, OrionEclipseClient, ProfilesListHolderModel, SchwabAccountDetailModel, SchwabClient } from 'src/app/core/clients/generated/client';
import { ToastClassEnum } from 'src/app/core/services/snackbar/snackbar.models';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { AccountUpdateMainModalComponent } from 'src/app/portal/features/account-request-workflows/modals/account-update-main-modal/account-update-main-modal.component';
import { Account, OrionAccountSleeveDetail } from '../../../../models/account.models';

@Component({
  selector: 'app-account-detail',
  templateUrl: './account-detail.component.html',
})
export class AccountDetailComponent implements OnInit, AfterViewInit {
  constructor (
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private schwabApiService: SchwabClient,
    private orionEclipsApiService: OrionEclipseClient,
    private investmentAccountApi: InvestmentAccountsClient,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
  ) { }

  isLoading = true;
  isLoading_schwab = true;
  isLoading_orion = true;
  accountId = 0;
  account?: AccountModel;
  custodianId?: number;
  schwabAccountModel? : SchwabAccountDetailModel;
  orionAccountsModel? : OrionEclipseAccountDetailModel[] = [];

  orionAccountSleeves? : OrionAccountSleeveDetail[] = [];
  schwabAccountIsLoaded = false;
  schwabLastUpdatedDate = new Date();
  orionLastUpdatedDate = '';
  orionTotalAccountValue = 0;
  selectedOrionAccountId? = '';
  positionSearchTerm = '';
  transactionSearchTerm = '';
  accountBeneficiaries? : ProfilesListHolderModel[] = [];
  accountHolders? : ProfilesListHolderModel[] = [];
  accountMoneyManagers? : ProfilesListHolderModel[] = [];

  ngOnInit(): void {
    this.titleService.setTitle(this.activatedRoute.snapshot.data['title'] + ' | Alphastar Portal');
    this.activatedRoute.paramMap.subscribe(params => {
      const id = params.get('id'); // Assuming the parameter name is 'id'
      if (id !== null) {
        this.accountId = parseInt(id);
      }
    });
  }

  ngAfterViewInit(): void {
    this.getAccountData();
  }

  getAccountData(): void {
    this.accountBeneficiaries = [];
    this.accountMoneyManagers = [];
    this.orionTotalAccountValue = 0;
    this.isLoading_schwab = true;
    this.isLoading_orion = true;
    this.schwabLastUpdatedDate = new Date();
    this.investmentAccountApi.getInvestmentAccount(this.accountId)
      .subscribe({
        next: (account) => {
          this.account = account;
          this.custodianId = this.account.custodianID;

          if (this.custodianId === 2) {

            this.schwabApiService.getAccountDetails(this.accountId)
              .subscribe({
                next: result => {
                  this.schwabAccountIsLoaded = true;
                  this.schwabAccountModel = result;
                  this.isLoading_schwab = false;


                  this.schwabAccountModel.profileList?.profiles.forEach((p) => {
                    p.holders.forEach(h => {
                      if (h.role === 'CONTB' || h.role === 'BENE') {
                        this.accountBeneficiaries?.push(h);
                      } else if (h.role === 'LPFA' || h.role === 'WPFA') {
                        this.accountMoneyManagers?.push(h);
                      } else if (h.role !== 'IND' && h.role !== 'VIEWO') {
                        this.accountHolders?.push(h);
                      }
                    });
                  });
                },
                error: () => {
                  this.schwabAccountIsLoaded = false;
                  this.isLoading_schwab = false;
                  // this.snackbarService.openSnackbar('Unable to retrieve Schwab account', ToastClassEnum.warning);
                }
              }).add(() => {
                this.isLoading_schwab = false;
              });
          } else {
            this.schwabAccountIsLoaded = false;
            this.isLoading_schwab = false;
          }

          this.orionEclipsApiService.getAccountDetails(this.accountId)
            .subscribe({
              next: result => {
                this.orionAccountsModel = result;
                this.orionLastUpdatedDate = result[0].accountValue.totalValueOn;

                result.forEach((orionAcct) => {

                  // 🔴 AN EMPTY SLEEVE IS NOT LISTED (AndyP, 2026-10-06). A sleeve carrying no
                  // managed value is scaffolding rather than a holding -- the Contribution and
                  // Distribution cash sleeves sit at zero on most accounts -- and a card per
                  // empty one pushes the real sleeves down the tab.
                  //
                  // ⚠️ FILTERED HERE, NOT IN THE TEMPLATE, so the tab agrees with itself: the
                  // Sleeves tab is gated on `orionAccountSleeves.length > 0`, and filtering in
                  // the `@for` instead would leave an account whose sleeves are all empty
                  // showing a Sleeves tab with nothing in it.
                  //
                  // ⚠️ `!== 0`, NOT `> 0`. A negative managed value is real and has to stay
                  // visible; `> 0` would hide the balance someone most needs to see.
                  if (orionAcct.summarySection.managedValue !== 0) {
                    const sleeve : OrionAccountSleeveDetail = {
                      Detail: orionAcct,
                      ShowPositions: false,
                    };

                    this.orionAccountSleeves?.push(sleeve);
                  }

                  // ⚠️ ACCUMULATED OUTSIDE THE FILTER, deliberately. The account total must count
                  // every sleeve Orion returned, listed or not -- a sleeve can hold zero MANAGED
                  // value and still carry excluded value, and dropping it from the total as well
                  // would make the figure at the top disagree with the account.
                  const totVal = orionAcct.summarySection.managedValue + orionAcct.summarySection.excludedValue;
                  this.orionTotalAccountValue += totVal;
                  if (orionAcct.model !== null || orionAcct.accountNumber === this.account?.accountNumber){
                    if (orionAcct.accountNumber === this.account?.accountNumber) {
                      this.selectedOrionAccountId = orionAcct.accountId;
                    }
                  }
                });
              },
              error: () => {
                this.isLoading_orion = false;
              }
            }).add(() => {
              this.isLoading_orion = false;
            });
        },
        error: () => {
          this.isLoading = false;
          this.snackbarService.openSnackbar('Unable to retrieve investment account', ToastClassEnum.warning);
        }
      }).add(() => {
        this.isLoading = false;
      });
  }

  openAccountUpdateModal(): void {
    if (this.account){
      const invAccount = this.convertInvestmentAccountToAccount(this.account);

      this.dialog.open<AccountUpdateMainModalComponent>(AccountUpdateMainModalComponent, {
        height: '72rem',
        minHeight: '50rem',
        data: invAccount,
      });
    }
  }

  convertInvestmentAccountToAccount(account: AccountModel): Account {
    return {
      Advisors: account.advisors.map(a => {
        return {
          AdvisorId: a.agentID,
          Crd: a.crd,
          FirstName: a.firstName,
          LastName: a.lastName,
        };
      }),
      Clients: account.clients.map(c => {
        return {
          Id: c.id,
          FirstName: c.firstName,
          LastName: c.lastName,
        };
      }),
      Type: account.investmentAccountType,
      Model: account.investmentAccountModel,
      Custodian: account.custodian,
      CustodianId: account.custodianID as number,
      Value: account.accountValue as number,
      CashValue: account.cashValue as number,
      OpenedDate: account.openedOn as Date,
      Fee: AccountDetailComponent.calculateAccountFee(account),
      Status: account.investmentAccountStatus,
      IsInvestmentAccount: true,
      AccountNumber: account.accountNumber,
      Carrier: null,
      Product: null,
      InsuranceAccountId: null,
      InvestmentAccountId: account.investmentAccountID,
      Sleeves: account.sleeves,
      ShowSleeves: false,
      TDSchwabAccountNumbers: account.tdSchwabAccountNumbers,
    };
  }

  private static calculateAccountFee(account: AccountModel): string {
    if (account.totalFee === 0) return '---';
    if (!account.sleeves.length) return formatPercent(account.totalFee ?? 0, 'en-US', '1.2');
    if (account.sleeves.every(a => a.accountTotalFee === account.sleeves[0].accountTotalFee)) // check if all sleeves have same fee
      return formatPercent(account.sleeves[0].accountTotalFee, 'en-US', '1.2');
    return 'Multiple';
  }
}
